import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, Pagination, Spinner, Alert } from 'react-bootstrap';
import { FaPlus, FaMinus, FaBalanceScale } from 'react-icons/fa';
import { creditAPI, adminAPI } from '../services/api';
import { toast } from 'react-toastify';

const CreditManagement = () => {
  const [balance, setBalance] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionMethod, setTransactionMethod] = useState('manual');
  const [notes, setNotes] = useState('');
  const [action, setAction] = useState('add');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [balanceRes, usersRes] = await Promise.all([
        creditAPI.getBalance(),
        adminAPI.getUsers({ limit: 1000 })
      ]);
      setBalance(balanceRes.data.data.credits);
      setUsers(usersRes.data.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser || !amount) {
      toast.error('Please select a user and enter amount');
      return;
    }

    try {
      const data = {
        user_id: parseInt(selectedUser),
        amount: parseInt(amount),
        transaction_method: transactionMethod,
        notes: notes
      };

      if (action === 'add') {
        await creditAPI.addCredits(data);
        toast.success('Credits added successfully');
      } else {
        await creditAPI.removeCredits(data);
        toast.success('Credits removed successfully');
      }

      fetchData();
      setAmount('');
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Transaction failed');
    }
  };

  if (loading) return <Spinner animation="border" />;

  return (
    <div>
      <h3 className="mb-4">Credit Management</h3>

      <Row>
        <Col md={4}>
          <Card className="mb-4">
            <Card.Body className="text-center">
              <FaBalanceScale size={48} className="text-primary mb-3" />
              <h4>Your Credit Balance</h4>
              <h2 className="text-primary">{balance}</h2>
              <p className="text-muted">Available Credits</p>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header><strong>Credit Transaction</strong></Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>Action</Form.Label>
                  <Form.Select value={action} onChange={(e) => setAction(e.target.value)}>
                    <option value="add">Add Credits</option>
                    <option value="remove">Remove Credits</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Select User</Form.Label>
                  <Form.Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} required>
                    <option value="">Choose user...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email}) - {u.credits} cr</option>)}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} required />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Transaction Method</Form.Label>
                  <Form.Select value={transactionMethod} onChange={(e) => setTransactionMethod(e.target.value)}>
                    <option value="manual">Manual</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="paypal">PayPal</option>
                    <option value="stripe">Stripe</option>
                    <option value="crypto">Cryptocurrency</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Form.Group>

                <Button variant={action === 'add' ? 'success' : 'danger'} type="submit" className="w-100">
                  {action === 'add' ? <><FaPlus /> Add Credits</> : <><FaMinus /> Remove Credits</>}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Card>
            <Card.Header><strong>Credit Packages</strong></Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Card className="text-center border-primary">
                    <Card.Body>
                      <h3>500</h3>
                      <p>Credits</p>
                      <h4>$50</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-success">
                    <Card.Body>
                      <h3>1100</h3>
                      <p>Credits (+100 Bonus)</p>
                      <h4>$100</h4>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-warning">
                    <Card.Body>
                      <h3>2300</h3>
                      <p>Credits (+300 Bonus)</p>
                      <h4>$200</h4>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <hr />

              <h5>Cost Reference</h5>
              <Table size="sm">
                <tbody>
                  <tr><td>Create Reseller</td><td>50 credits</td></tr>
                  <tr><td>6 Months License</td><td>10 credits</td></tr>
                  <tr><td>1 Year License</td><td>20 credits</td></tr>
                  <tr><td>3 Years License</td><td>30 credits</td></tr>
                  <tr><td>Trial License</td><td>0 credits</td></tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CreditManagement;
